// TransferSummaryBar.jsx
// Sticky bottom bar that shows running totals during the Amount step.
// Mirrors the ZapFeed "you're all caught up" bar style — minimal, information-dense.
// Shows: recipient count · total · balance status · continue CTA
import React from "react";
import { Box, Typography, Button, Stack, Chip, LinearProgress } from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  RequestQuote as RequestIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { fmtUSD } from "./T2a.utils";

/**
 * Props
 * ─────
 * recipients   : Recipient[]
 * totalAmount  : number
 * balance      : number
 * allAmountsSet: boolean
 * onContinue   : () => void
 * mode         : "pay" | "request"   (default: "pay")
 */
export function TransferSummaryBar({ recipients, totalAmount, balance, allAmountsSet, onContinue, mode = "pay" }) {
  const theme     = useTheme();
  const isRequest = mode === "request";

  // In request mode balance is irrelevant — never show over-balance warning
  const overBal   = !isRequest && totalAmount > balance;
  const pct       = !isRequest && balance > 0 ? Math.min((totalAmount / balance) * 100, 100) : 0;

  // Color logic
  const accentMain = isRequest
    ? theme.palette.success.main
    : overBal
    ? theme.palette.warning.main
    : theme.palette.primary.main;

  const accentDark = isRequest
    ? theme.palette.success.dark
    : overBal
    ? theme.palette.warning.dark
    : theme.palette.primary.dark;

  const borderColor = isRequest
    ? alpha(theme.palette.success.main, 0.3)
    : overBal
    ? alpha(theme.palette.warning.main, 0.35)
    : alpha(theme.palette.success.main, 0.3);

  const bgColor = isRequest
    ? alpha(theme.palette.success.main, 0.04)
    : overBal
    ? alpha(theme.palette.warning.main, 0.04)
    : alpha(theme.palette.success.main, 0.04);

  // CTA label
  const ctaLabel = !allAmountsSet
    ? "Enter all amounts to continue"
    : isRequest
    ? `Request ${fmtUSD(totalAmount)}`
    : overBal
    ? `Continue — choose how to pay ${fmtUSD(totalAmount)}`
    : `Transfer ${fmtUSD(totalAmount)} from balance`;

  if (recipients.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor,
        bgcolor: bgColor,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* CTA */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        disabled={!allAmountsSet}
        onClick={onContinue}
        startIcon={isRequest && allAmountsSet ? <RequestIcon /> : undefined}
        endIcon={<ArrowForwardIcon />}
        sx={{
          height: 46,
          borderRadius: 2.5,
          fontWeight: 700,
          fontSize: "0.95rem",
          background: allAmountsSet
            ? `linear-gradient(135deg, ${accentDark}, ${accentMain})`
            : undefined,
          boxShadow: allAmountsSet
            ? `0 4px 14px ${alpha(accentMain, 0.35)}`
            : "none",
          "&:hover": {
            boxShadow: allAmountsSet
              ? `0 6px 20px ${alpha(accentMain, 0.45)}`
              : "none",
            transform: allAmountsSet ? "translateY(-1px)" : "none",
          },
          transition: "all 0.2s ease",
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  );
}